"use client";

import { useMemo, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  ambiguousWords,
  checkPattern,
  findSeasonWords,
  type LineCheck,
  nextReading,
  PATTERNS,
  type PatternId,
  parsePattern,
} from "@/lib/tool-kit/poetry/haiku";
import {
  chosenReading,
  type Overrides,
  poemLines,
  type WordInfo,
} from "@/lib/tool-kit/poetry/pronounce";
import {
  POETRY_READING_STRINGS,
  REASON_KEYS,
} from "@/lib/tool-kit/poetry/strings";
import { usePoetryDictionary } from "@/lib/tool-kit/poetry/use-dictionary";

/** English strings. A translated content file overrides these through `ui`. */
export const HAIKU_CHECKER_STRINGS = {
  ...POETRY_READING_STRINGS,
  textLabel: "Your poem — one line of verse per line",
  textPlaceholder: "Old pond\nfrogs jumping in\nsound of water",
  trySample: "Try a sample",
  sample: "Old pond\nfrogs jumping in\nsound of water",
  sampleCredit:
    "Sample: Bashō’s “old pond” hokku in Lafcadio Hearn’s 1898 English translation (public domain).",
  clear: "Clear",
  patternLegend: "Syllable pattern",
  patternClassic: "5-7-5 (traditional English)",
  patternShort: "3-5-3 (short-long-short)",
  patternCustom: "Custom pattern",
  customLabel: "Syllables per line",
  customPlaceholder: "5, 7, 5",
  customHint:
    "Numbers separated by commas or dashes, one per line — 5, 7, 5, 7, 7 for a tanka.",
  customInvalid: "Enter between one and twelve line lengths, such as 5, 7, 5.",
  emptyPrompt: "Type or paste a poem to count its syllables line by line.",
  heading: "Syllable check",
  verdictMatch: "This poem matches {pattern}.",
  verdictPossible:
    "Close: this matches {pattern} if you read the dashed-outline words the other way.",
  verdictOff: "This poem does not match {pattern} yet.",
  totals: "{total} syllables in {lines}; the pattern has {target}.",
  lineCount: "one:{count} line|other:{count} lines",
  syllables: "one:{count} syllable|other:{count} syllables",
  lineHeading: "Line {number}",
  statusMatch: "Matches",
  statusOver: "{amount} over",
  statusUnder: "{amount} short",
  statusPossible: "Reaches {target} with another reading",
  statusExtra: "Beyond the pattern",
  countOfTarget: "{count} of {target}",
  countOnly: "{count}",
  missingLines:
    "one:The pattern has {count} more line than your poem.|other:The pattern has {count} more lines than your poem.",
  wordsHeading: "Word by word",
  wordsHint:
    "Words outlined with a dashed border can be read more than one way — press one to switch its count. A tilde means the count is estimated.",
  chipLabel: "{word}: {count}. Press to read it as {next}.",
  estimatedMark: "~",
  ambiguousHeading: "Words that can be read more than one way",
  ambiguousItem: "{word} — {default}, or {others}",
  seasonHeading: "Seasonal words",
  seasonFound: "{word} ({season})",
  seasonNone:
    "No seasonal word found among the ones this tool knows. Traditional Japanese haiku carry a kigo, but English-language haiku often do not, so this is a note, not a fault.",
  seasonNote:
    "A sample of seasonal words from the Japanese saijiki tradition — in that tradition the moon on its own is an autumn word and singing frogs mean spring. The list is not complete.",
  seasonSpring: "spring",
  seasonSummer: "summer",
  seasonAutumn: "autumn",
  seasonWinter: "winter",
  copySummary: "Copy line counts",
  copied: "Copied!",
  summaryLine: "Line {number}: {count} (target {target}) — {text}",
  summaryLineFree: "Line {number}: {count} — {text}",
} as const;

type Strings = typeof HAIKU_CHECKER_STRINGS;

const ID = "haiku";

function statusText(
  check: LineCheck,
  strings: Strings,
  fmt: (n: number) => string,
  locale: string
): string {
  switch (check.status) {
    case "match":
      return strings.statusMatch;
    case "over":
      return t(strings.statusOver, {
        amount: tPlural(strings.syllables, locale, check.difference, {
          count: fmt(check.difference),
        }),
      });
    case "under":
      return t(strings.statusUnder, {
        amount: tPlural(strings.syllables, locale, -check.difference, {
          count: fmt(-check.difference),
        }),
      });
    case "possible":
      return t(strings.statusPossible, { target: fmt(check.target ?? 0) });
    default:
      return strings.statusExtra;
  }
}

function WordChip({
  word,
  overrides,
  onPick,
  strings,
}: {
  word: WordInfo;
  overrides: Overrides;
  onPick: (key: string, syllables: number) => void;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const count = chosenReading(word, overrides).syllables;
  const estimated = word.source === "estimated";
  const mark = estimated ? strings.estimatedMark : "";
  const base = "rounded-lg border bg-card px-3 py-1.5 text-sm";
  if (word.alternatives.length === 0) {
    return (
      <span className={`${base} border-border`}>
        <span className="text-foreground">{word.text}</span>
        <span className="ml-2 font-semibold text-primary">
          {mark}
          {fmt(count)}
        </span>
      </span>
    );
  }
  const next = nextReading(word, overrides);
  return (
    <button
      aria-label={t(strings.chipLabel, {
        word: word.text,
        count: fmt(count),
        next: fmt(next),
      })}
      className={`${base} border-primary/60 border-dashed hover:bg-muted`}
      onClick={() => onPick(word.key, next)}
      title={estimated ? strings.sourceEstimated : strings.wordsHint}
      type="button"
    >
      <span className="text-foreground">{word.text}</span>
      <span className="ml-2 font-semibold text-primary">
        {mark}
        {fmt(count)}
      </span>
    </button>
  );
}

function LineCard({
  check,
  number,
  overrides,
  onPick,
  strings,
}: {
  check: LineCheck;
  number: number;
  overrides: Overrides;
  onPick: (key: string, syllables: number) => void;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const good = check.status === "match";
  const count =
    check.target === null
      ? t(strings.countOnly, { count: fmt(check.syllables) })
      : t(strings.countOfTarget, {
          count: fmt(check.syllables),
          target: fmt(check.target),
        });
  return (
    <li className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="font-medium text-foreground text-sm">
          {t(strings.lineHeading, { number: fmt(number) })}
          <span className="ml-2 font-normal text-muted-foreground">
            {check.line.text.trim()}
          </span>
        </h4>
        <p className="font-semibold text-foreground text-sm">
          {count}
          <span
            className={
              good
                ? "ml-2 font-normal text-emerald-700 dark:text-emerald-400"
                : "ml-2 font-normal text-muted-foreground"
            }
          >
            {statusText(check, strings, fmt, locale)}
          </span>
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {check.line.words.map((word) => (
          <WordChip
            key={`${word.start}-${word.key}`}
            onPick={onPick}
            overrides={overrides}
            strings={strings}
            word={word}
          />
        ))}
      </div>
    </li>
  );
}

function AmbiguousList({
  words,
  strings,
}: {
  words: WordInfo[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const say = (n: number) =>
    tPlural(strings.syllables, locale, n, { count: fmt(n) });
  return (
    <section className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.ambiguousHeading}</h3>
      <ul className="space-y-2 text-sm">
        {words.map((word) => {
          const reasons = [
            ...new Set(
              word.alternatives.map(
                (alt) => strings[REASON_KEYS[alt.reason ?? "estimate"]]
              )
            ),
          ];
          return (
            <li key={word.key}>
              <span className="font-medium text-foreground">
                {t(strings.ambiguousItem, {
                  word: word.text,
                  default: say(word.reading.syllables),
                  others: word.alternatives
                    .map((alt) => say(alt.syllables))
                    .join(", "),
                })}
              </span>
              <span className="block text-muted-foreground text-xs">
                {reasons.join(" ")}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default function HaikuCheckerWidget() {
  const strings = useToolUi(HAIKU_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [pattern, setPattern] = useState<PatternId>("classic");
  const [custom, setCustom] = useState("5, 7, 5, 7, 7");
  const [overrides, setOverrides] = useState<Overrides>({});
  const hasText = text.trim().length > 0;
  const { lookup, status } = usePoetryDictionary(hasText);

  const target = useMemo(
    () => (pattern === "custom" ? parsePattern(custom) : PATTERNS[pattern]),
    [pattern, custom]
  );
  const lines = useMemo(() => poemLines(text, lookup), [text, lookup]);
  const check = useMemo(
    () => (target ? checkPattern(lines, target, overrides) : null),
    [lines, target, overrides]
  );
  const ambiguous = useMemo(() => ambiguousWords(lines), [lines]);
  const seasons = useMemo(() => findSeasonWords(lines), [lines]);
  const patternLabel = target ? target.join("-") : "";

  function pick(key: string, syllables: number) {
    setOverrides((previous) => ({ ...previous, [key]: syllables }));
  }

  const summary = check
    ? check.lines
        .map((entry, index) =>
          t(
            entry.target === null
              ? strings.summaryLineFree
              : strings.summaryLine,
            {
              number: fmt(index + 1),
              count: fmt(entry.syllables),
              target: fmt(entry.target ?? 0),
              text: entry.line.text.trim(),
            }
          )
        )
        .join("\n")
    : "";

  let verdictText = "";
  if (check) {
    const template = {
      match: strings.verdictMatch,
      possible: strings.verdictPossible,
      off: strings.verdictOff,
    }[check.verdict];
    verdictText = t(template, { pattern: patternLabel });
  }

  return (
    <>
      <TextAreaField
        id={`${ID}-text`}
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={5}
        value={text}
      />
      <PillGroup
        label={strings.patternLegend}
        name={`${ID}-pattern`}
        onChange={setPattern}
        options={[
          { value: "classic", label: strings.patternClassic },
          { value: "short", label: strings.patternShort },
          { value: "custom", label: strings.patternCustom },
        ]}
        value={pattern}
      />
      {pattern === "custom" ? (
        <TextField
          hint={strings.customHint}
          id={`${ID}-custom`}
          label={strings.customLabel}
          mono
          onChange={setCustom}
          placeholder={strings.customPlaceholder}
          value={custom}
        />
      ) : null}
      {target ? null : <ErrorNotice>{strings.customInvalid}</ErrorNotice>}
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setText(strings.sample);
            setOverrides({});
          }}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={!hasText}
          onClick={() => {
            setText("");
            setOverrides({});
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {text === strings.sample ? (
        <p className={HINT}>{strings.sampleCredit}</p>
      ) : null}

      {status === "loading" ? (
        <Notice>{strings.dictionaryLoading}</Notice>
      ) : null}
      {status === "failed" ? (
        <ErrorNotice>{strings.dictionaryFailed}</ErrorNotice>
      ) : null}

      {check && lines.length > 0 ? (
        <section aria-live="polite" className="space-y-5">
          <div className="space-y-2">
            <h2 className={WIDGET_H2}>{strings.heading}</h2>
            <div className={RESULT_CARD}>
              <p className="font-semibold text-foreground">{verdictText}</p>
              <p className="mt-1 text-muted-foreground text-sm">
                {t(strings.totals, {
                  total: tPlural(strings.syllables, locale, check.total, {
                    count: fmt(check.total),
                  }),
                  lines: tPlural(strings.lineCount, locale, lines.length, {
                    count: fmt(lines.length),
                  }),
                  target: fmt(check.targetTotal),
                })}
              </p>
            </div>
            {check.missingLines > 0 ? (
              <Notice>
                {tPlural(strings.missingLines, locale, check.missingLines, {
                  count: fmt(check.missingLines),
                })}
              </Notice>
            ) : null}
          </div>
          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.wordsHeading}</h3>
            <p className={HINT}>{strings.wordsHint}</p>
            <ol className="space-y-3">
              {check.lines.map((entry, index) => (
                <LineCard
                  check={entry}
                  key={`${entry.line.number}`}
                  number={index + 1}
                  onPick={pick}
                  overrides={overrides}
                  strings={strings}
                />
              ))}
            </ol>
          </div>
          {ambiguous.length > 0 ? (
            <AmbiguousList strings={strings} words={ambiguous} />
          ) : null}
          <section className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.seasonHeading}</h3>
            {seasons.length > 0 ? (
              <p className="text-foreground text-sm">
                {seasons
                  .map((found) =>
                    t(strings.seasonFound, {
                      word: found.word,
                      season: {
                        spring: strings.seasonSpring,
                        summer: strings.seasonSummer,
                        autumn: strings.seasonAutumn,
                        winter: strings.seasonWinter,
                      }[found.season],
                    })
                  )
                  .join(", ")}
              </p>
            ) : (
              <p className="text-muted-foreground text-sm">
                {strings.seasonNone}
              </p>
            )}
            <p className={HINT}>{strings.seasonNote}</p>
          </section>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copySummary}
            text={summary}
          />
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
